import { useEffect } from 'react';

import { useLocation } from 'react-router-dom';

import {
  CALCULATOR_SCHEMA_ID,
  calculatorStructuredDataGraph
} from '../features/calculators/structuredData';
import { learnHeadData } from '../features/learn/headData';
import { LEARN_SCHEMA_ID, learnStructuredDataGraph } from '../features/learn/structuredData';
import {
  LOAN_PROGRAM_SCHEMA_ID,
  loanProgramStructuredDataGraph
} from '../features/loan-programs/structuredData';
import { MORTGAGE_RATES_SCHEMA_ID, mortgageRatesStructuredDataGraph } from '../features/mortgage-rates/structuredData';
import {
  PREAPPROVAL_SCHEMA_ID,
  preapprovalStructuredDataGraph
} from '../features/preapproval-guide/structuredData';
import {
  canonicalUrl,
  getPublicRoute,
  isHeldRoute,
  isLoanOfficerProfilePath,
  isRateQuotePath
} from '../lib/analytics/publicRoutes';
import { removeMeta, setCanonical, upsertMeta } from '../lib/headMeta';

const NOT_FOUND_TITLE = 'Page Not Found | Lendwise Mortgage';
const NOT_FOUND_DESCRIPTION = 'The requested page could not be found.';

/**
 * Keeps head metadata accurate during client-side navigation. The production
 * build writes the same values into each prerendered HTML document so crawlers
 * and no-JavaScript clients never depend on this effect.
 */
export function RouteMetadata() {
  const location = useLocation();

  useEffect(() => {
    const existingProgramSchema = document.getElementById(LOAN_PROGRAM_SCHEMA_ID);

    // Learn article tags are a projection of the catalog, applied on every
    // navigation BEFORE the officer early return: the projection is null for
    // the index, unknown Learn paths, production holds, officer pages and
    // every ordinary route, so article tags can never linger after leaving
    // an article, whatever the destination.
    const article = learnHeadData(location.pathname, { dev: import.meta.env.DEV });
    if (article) {
      upsertMeta('property', 'og:type', 'article');
      upsertMeta('property', 'article:published_time', article.publishedOn);
      upsertMeta('property', 'article:modified_time', article.updatedOn);
    } else {
      upsertMeta('property', 'og:type', 'website');
      removeMeta('property', 'article:published_time');
      removeMeta('property', 'article:modified_time');
    }

    // Officer profile pages own their head statically: the prerender fan-out
    // writes PER-OFFICER title/robots/canonical (indexability is per profile,
    // which this registry cannot know synchronously). Overwriting here would
    // flip an approved page to noindex under Google's JS rendering. The
    // profile page component manages document.title for SPA navigation.
    if (isLoanOfficerProfilePath(location.pathname)) {
      existingProgramSchema?.remove();
      document.getElementById(CALCULATOR_SCHEMA_ID)?.remove();
      document.getElementById(PREAPPROVAL_SCHEMA_ID)?.remove();
      document.getElementById(MORTGAGE_RATES_SCHEMA_ID)?.remove();
      document.getElementById(LEARN_SCHEMA_ID)?.remove();
      return;
    }

    // A token-bearing quote path is a real private page, not a 404. It borrows
    // the '/rate-quote' entry's title and noindex robots value; the token never
    // reaches the head, because that entry carries no canonical.
    // A held route is registered but not rendered in production, so its 404
    // must not inherit unpublished metadata. Development is the intentional
    // review surface: the route is mounted there and should carry its real
    // title plus noindex robots while the publication hold remains intact.
    const cancellation = location.pathname === '/rate-experience'
      && new URLSearchParams(location.search).get('view') === 'unsubscribe';
    const route = isHeldRoute(location.pathname) && !import.meta.env.DEV && !cancellation
      ? undefined
      : getPublicRoute(isRateQuotePath(location.pathname) ? '/rate-quote' : location.pathname);
    const title = cancellation ? 'Stop Rate Watch updates | Lendwise Mortgage' : route?.title ?? NOT_FOUND_TITLE;
    const description = cancellation ? 'Confirm cancellation of your Lendwise Rate Watch updates.' : route?.description ?? NOT_FOUND_DESCRIPTION;
    const indexable = route?.indexable === true;
    const canonical = route && indexable ? canonicalUrl(route) : null;

    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', indexable ? 'index, follow' : 'noindex, nofollow, noarchive');
    upsertMeta('property', 'og:site_name', 'Lendwise Mortgage');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('name', 'twitter:card', 'summary');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);

    if (canonical) {
      upsertMeta('property', 'og:url', canonical);
    } else {
      document.head.querySelector<HTMLMetaElement>('meta[property="og:url"]')?.remove();
    }
    setCanonical(canonical);

    // All public schema families use the same synchronous writer. Keep the
    // markup in prerendered HTML and avoid duplicating it in the shared shell.
    const schemas: Array<[string, object | null]> = [
      [LOAN_PROGRAM_SCHEMA_ID, loanProgramStructuredDataGraph(location.pathname)],
      [CALCULATOR_SCHEMA_ID, calculatorStructuredDataGraph(location.pathname)],
      [PREAPPROVAL_SCHEMA_ID, preapprovalStructuredDataGraph(location.pathname)],
      [MORTGAGE_RATES_SCHEMA_ID, mortgageRatesStructuredDataGraph(location.pathname)],
      [LEARN_SCHEMA_ID, learnStructuredDataGraph(location.pathname)],
    ];
    for (const [id, graph] of schemas) {
      const existing = document.getElementById(id);
      if (!graph) {
        existing?.remove();
        continue;
      }
      const script = existing instanceof HTMLScriptElement ? existing : document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(graph).replace(/</g, '\\u003c');
      if (!script.isConnected) document.head.appendChild(script);
    }
  }, [location.pathname, location.search]);

  return null;
}
